import { LinkButton } from '@understory-io/leaf/link-button';
import { Stack } from '@understory-io/leaf/stack';
import { Text } from '@understory-io/leaf/text';
import { css } from '@understory-io/leaf/css';
import { FormattedMessage } from 'react-intl';
import { ExperienceCard } from '~/ui/experience-card';
import { EventTime, firstLetterCapStyle } from '~/ui/event-time';
import type { UpcomingEventCard } from './page.server';
import { trackBookClicked, trackReadMoreClicked } from './tracking';

/** `select_item` list for cards on this page, so GTM can tell them apart from
 *  the home grid's. */
const LIST = { id: 'upcomingEvents', name: 'Upcoming Events' };

/**
 * The page's event list: one `ExperienceCard` per event in a responsive grid.
 *
 * The rows are events, not experiences, so an experience with three dates
 * appears three times — each card carries that date in the subtitle slot and
 * a Book button that opens the dialog on it.
 *
 * An ordered list, because the feed is sorted by start time and that order is
 * meaningful — the soonest event first.
 */
export function EventGrid({
  events,
  showFromPrice,
}: {
  events: UpcomingEventCard[];
  showFromPrice: boolean;
}) {
  return (
    <ol className={gridStyles}>
      {events.map((event, index) => {
        const isSoldOut = event.status === 'soldOut';
        return (
          <li key={event.eventId} className={itemStyles}>
            <ExperienceCard
              id={event.experienceId}
              name={event.experienceName}
              image={event.image}
              price={event.price}
              locations={event.locations}
              showFromPrice={showFromPrice}
              // Deliberately not passed through: the card shows ONE subtitle
              // line and prefers the owner label over `datesSlot`, which here
              // would hide the very thing each row is about. The label is
              // composed into the slot below instead.
              ownerHostName={null}
              datesSlot={
                <EventSubtitle
                  sessions={event.sessions}
                  ownerHostName={event.ownerHostName}
                />
              }
              list={LIST}
              // The legacy page tracked this on an explicit "Read more"
              // button; this card's whole surface is that link.
              onLinkClick={() => trackReadMoreClicked(event, index)}
            >
              <LinkButton
                href={event.bookHref}
                appearance="primary"
                size="large"
                disabled={isSoldOut}
                onClick={() => trackBookClicked(event, index)}
              >
                <FormattedMessage
                  defaultMessage="Book"
                  description="Button on an upcoming date card that starts the checkout flow"
                />
              </LinkButton>
            </ExperienceCard>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * The card's subtitle line: this event's date, preceded by the owner
 * attribution when the experience is shared from another storefront.
 *
 * Both belong to the one slot the card gives a subtitle, so they are stacked
 * here rather than fought over.
 */
function EventSubtitle({
  sessions,
  ownerHostName,
}: {
  sessions: UpcomingEventCard['sessions'];
  ownerHostName: string | null;
}) {
  return (
    <Stack gap="tiny">
      {ownerHostName && (
        <Text as="span" style="body-small-regular" color="tertiary">
          <FormattedMessage
            defaultMessage="by {hostName}"
            description="Experience host name for a shared Connect experience"
            values={{ hostName: ownerHostName }}
          />
        </Text>
      )}
      <EventTime sessions={sessions}>
        {({ title, details }) => (
          <Text
            as="span"
            style="body-small-regular"
            color="tertiary"
            className={firstLetterCapStyle}
            suppressHydrationWarning
          >
            {title}
            {details ? ` · ${details}` : ''}
          </Text>
        )}
      </EventTime>
    </Stack>
  );
}

// 1 / 2 / 3 columns, matching the legacy page's xs=12 sm=6 lg=4 grid.
// `minmax(0, 1fr)` rather than `1fr` so a long experience name can't push a
// column wider than its share.
const gridStyles = css({
  listStyle: 'none',
  margin: 'none',
  padding: 'none',
  display: 'grid',
  gap: 'large',
  gridTemplateColumns: {
    base: '[1fr]',
    tablet: '[repeat(2, minmax(0, 1fr))]',
    desktop: '[repeat(3, minmax(0, 1fr))]',
  },
});

// The card fills its cell, so cards in a row are equal height and their Book
// buttons line up.
const itemStyles = css({
  display: 'flex',
  minWidth: '[0]',
});
